import React, { useEffect, useState } from 'react';
import crmClient from './crmClient';
import { formatDate } from './format';

// Slide-in detail/edit panel for a contact (mirrors the company detail panel UX).
const ContactDetail = ({ contact, meta, onClose, onChanged }) => {
  const [form, setForm] = useState(contact);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(contact);
    setSaved(false);
    setError(null);
  }, [contact]);

  const contactTypes = (meta && meta.contact_types) || ['prospect', 'merchant', 'driver', 'investor'];

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleType = type =>
    setForm(prev => {
      const list = Array.isArray(prev.contact_types) ? prev.contact_types : [];
      const next = list.includes(type) ? list.filter(t => t !== type) : [...list, type];
      return { ...prev, contact_types: next.length ? next : ['prospect'] };
    });

  const save = () => {
    setBusy(true);
    setError(null);
    crmClient
      .updateContact(contact.crm_contact_id, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        title: form.title,
        contact_types: form.contact_types,
        email_subscription_status: form.email_subscription_status,
        do_not_call: form.do_not_call,
        do_not_email: form.do_not_email,
        do_not_text: form.do_not_text,
        next_follow_up_at: form.next_follow_up_at || null,
        notes: form.notes
      })
      .then(() => {
        setBusy(false);
        setSaved(true);
        if (onChanged) onChanged();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to save.');
      });
  };

  const name = [form.first_name, form.last_name].filter(Boolean).join(' ') || form.email || 'Unnamed contact';

  return (
    <aside className="crm-detail">
      <div className="crm-detail-head">
        <div>
          <div className="crm-chip-row" style={{ margin: 0 }}>
            {(form.contact_types || []).map(t => (
              <span key={t} className="crm-chip crm-chip--on" style={{ cursor: 'default' }}>
                {t}
              </span>
            ))}
          </div>
          <h2>{name}</h2>
          <p className="crm-detail-sub">
            {form.email || '—'}
            {form.phone ? ` · ${form.phone}` : ''}
          </p>
        </div>
        <button type="button" className="crm-icon-btn" onClick={onClose} aria-label="Close contact">
          ✕
        </button>
      </div>

      {error && <div className="crm-inline-error">{error}</div>}

      <section className="crm-detail-section">
        <h3>Details</h3>
        <div className="crm-field-row">
          <label>
            <span>First name</span>
            <input value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} />
          </label>
          <label>
            <span>Last name</span>
            <input value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} />
          </label>
        </div>
        <div className="crm-field-row">
          <label>
            <span>Email</span>
            <input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} />
          </label>
          <label>
            <span>Phone</span>
            <input value={form.phone || ''} onChange={e => set('phone', e.target.value)} />
          </label>
          <label>
            <span>Title</span>
            <input value={form.title || ''} onChange={e => set('title', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="crm-detail-section">
        <h3>Type</h3>
        <div className="crm-chip-row">
          {contactTypes.map(type => (
            <button
              type="button"
              key={type}
              className={`crm-chip${(form.contact_types || []).includes(type) ? ' crm-chip--on' : ''}`}
              onClick={() => toggleType(type)}
            >
              {type}
            </button>
          ))}
        </div>
      </section>

      <section className="crm-detail-section">
        <h3>Communication preferences</h3>
        <div className="crm-field-row">
          <label>
            <span>Email subscription</span>
            <select value={form.email_subscription_status || 'subscribed'} onChange={e => set('email_subscription_status', e.target.value)}>
              <option value="subscribed">Subscribed</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </label>
        </div>
        <div className="crm-checkbox-row">
          <label>
            <input type="checkbox" checked={!!form.do_not_call} onChange={e => set('do_not_call', e.target.checked)} /> Do not call
          </label>
          <label>
            <input type="checkbox" checked={!!form.do_not_email} onChange={e => set('do_not_email', e.target.checked)} /> Do not email
          </label>
          <label>
            <input type="checkbox" checked={!!form.do_not_text} onChange={e => set('do_not_text', e.target.checked)} /> Do not text
          </label>
        </div>
      </section>

      <section className="crm-detail-section">
        <h3>Notes</h3>
        <textarea rows={4} value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Notes about this contact…" />
        <p className="crm-muted">Added {formatDate(contact.created_at)}</p>
      </section>

      <div className="crm-detail-actions">
        <button type="button" className="crm-btn" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span className="crm-muted">Saved</span>}
      </div>
    </aside>
  );
};

export default ContactDetail;
